<template>
  <div class="mon-page">
    <ToastPrime position="top-right" />
    <header class="mon-hero">
      <p class="mon-eyebrow">Operaciones</p>
      <h1 class="mon-title">Monitoreo del servidor</h1>
      <p class="mon-lead">
        Vista de proceso .NET, tráfico reciente del API, uso aproximado de administradores, cola del archivo de
        log4net (errores en disco) y consultas dinámicas de SQL Server. Los contadores del API viven en memoria del
        servidor y se pueden reiniciar.
      </p>
    </header>

    <div class="mon-actions">
      <ButtonPrime
        type="button"
        label="Actualizar todo"
        icon="pi pi-refresh"
        :loading="loadingAll"
        @click="cargarTodo"
      />
      <ButtonPrime
        type="button"
        label="Reiniciar métricas API"
        icon="pi pi-trash"
        class="p-button-secondary"
        outlined
        :loading="loadingReset"
        @click="confirmarReinicio"
      />
      <ButtonPrime
        type="button"
        label="Solo logs (archivo)"
        icon="pi pi-file"
        outlined
        :loading="loadingLog"
        @click="cargarSoloLogs"
      />
      <div class="mon-log-params">
        <label class="mon-log-label">Líneas</label>
        <select v-model.number="logMaxLines" class="mon-select" @change="cargarSoloLogs">
          <option :value="200">200</option>
          <option :value="400">400</option>
          <option :value="800">800</option>
          <option :value="1200">1200</option>
        </select>
        <label class="mon-log-label">Máx. KB leídos</label>
        <select v-model.number="logMaxKb" class="mon-select" @change="cargarSoloLogs">
          <option :value="256">256</option>
          <option :value="512">512</option>
          <option :value="1024">1024</option>
          <option :value="2048">2048</option>
        </select>
      </div>
    </div>

    <MessagePrime v-if="errorMsg" severity="error" class="mon-msg" :closable="true" @close="errorMsg = ''">
      {{ errorMsg }}
    </MessagePrime>

    <div class="mon-grid">
      <CardPrime class="surface-card mon-card">
        <template #title>
          <span class="mon-card-title"><i class="pi pi-desktop" aria-hidden="true" /> Proceso backend</span>
        </template>
        <template #content>
          <dl v-if="summary" class="mon-dl">
            <dt>Servidor (UTC)</dt>
            <dd>{{ fmtUtc(summary.serverUtc) }}</dd>
            <dt>Máquina</dt>
            <dd>{{ summary.machineName }}</dd>
            <dt>Working set</dt>
            <dd>{{ summary.process?.workingSetMb }} MB</dd>
            <dt>Memoria privada</dt>
            <dd>{{ summary.process?.privateMemoryMb }} MB</dd>
            <dt>Hilos</dt>
            <dd>{{ summary.process?.threadCount }}</dd>
            <dt>GC (managed aprox.)</dt>
            <dd>{{ summary.gcTotalMemoryMb }} MB</dd>
            <dt>Inicio proceso (UTC)</dt>
            <dd>{{ fmtUtc(summary.process?.startedUtc) }}</dd>
          </dl>
          <p v-else class="mon-muted">Sin datos. Pulsa «Actualizar todo».</p>
        </template>
      </CardPrime>

      <CardPrime class="surface-card mon-card">
        <template #title>
          <span class="mon-card-title"><i class="pi pi-chart-bar" aria-hidden="true" /> API (memoria)</span>
        </template>
        <template #content>
          <dl v-if="summary?.api" class="mon-dl">
            <dt>Peticiones registradas</dt>
            <dd>{{ summary.api.totalRequestsRecorded }}</dd>
            <dt>Tiempo medio (registrado)</dt>
            <dd>{{ roundMs(summary.api.averageDurationMsRecorded) }} ms</dd>
            <dt>Petición en curso (aprox.)</dt>
            <dd>{{ summary.api.activeRequests }}</dd>
            <dt>Usuarios admin activos (~)</dt>
            <dd>
              {{ summary.api.concurrentUsersApprox }}
              <span class="mon-hint">(últimos {{ summary.api.concurrentUserWindowMinutes }} min, por id de usuario en JWT)</span>
            </dd>
          </dl>
          <p v-else class="mon-muted">Sin datos.</p>
        </template>
      </CardPrime>

      <CardPrime class="surface-card mon-card mon-card--wide">
        <template #title>
          <span class="mon-card-title"><i class="pi pi-globe" aria-hidden="true" /> Navegador (front)</span>
        </template>
        <template #content>
          <dl class="mon-dl">
            <dt>URL</dt>
            <dd class="mon-wrap">{{ client.href }}</dd>
            <dt>API configurada</dt>
            <dd class="mon-wrap">{{ client.apiBase }}</dd>
            <dt>User agent</dt>
            <dd class="mon-wrap">{{ client.userAgent }}</dd>
            <dt v-if="client.jsHeapMb != null">JS heap (Chrome)</dt>
            <dd v-if="client.jsHeapMb != null">{{ client.jsHeapMb }} MB usados / {{ client.jsHeapLimitMb }} MB límite</dd>
          </dl>
        </template>
      </CardPrime>
    </div>

    <CardPrime class="surface-card mon-card mon-card--full">
      <template #title>
        <span class="mon-card-title"><i class="pi pi-list" aria-hidden="true" /> Rutas más llamadas</span>
      </template>
      <template #content>
        <DataTable
          v-if="summary?.api?.topRoutes?.length"
          :value="summary.api.topRoutes"
          size="small"
          stripedRows
          responsiveLayout="scroll"
        >
          <ColumnPrime field="routeKey" header="Ruta" />
          <ColumnPrime field="count" header="Conteo" />
        </DataTable>
        <p v-else class="mon-muted">Sin datos o contadores reiniciados.</p>
      </template>
    </CardPrime>

    <CardPrime class="surface-card mon-card mon-card--full">
      <template #title>
        <span class="mon-card-title"><i class="pi pi-history" aria-hidden="true" /> Últimas peticiones HTTP</span>
      </template>
      <template #content>
        <DataTable
          v-if="summary?.api?.recentRequests?.length"
          :value="summary.api.recentRequests"
          size="small"
          stripedRows
          paginator
          :rows="15"
          responsiveLayout="scroll"
        >
          <ColumnPrime field="utc" header="Hora (UTC)">
            <template #body="{ data }">{{ fmtUtc(data.utc) }}</template>
          </ColumnPrime>
          <ColumnPrime field="method" header="Método" style="width: 5rem" />
          <ColumnPrime field="routeKey" header="Ruta" />
          <ColumnPrime field="statusCode" header="HTTP" style="width: 4rem" />
          <ColumnPrime field="elapsedMs" header="ms" style="width: 4rem" />
          <ColumnPrime field="userId" header="Usuario id" style="width: 8rem" />
        </DataTable>
        <p v-else class="mon-muted">Sin tráfico registrado aún.</p>
      </template>
    </CardPrime>

    <CardPrime class="surface-card mon-card mon-card--full">
      <template #title>
        <span class="mon-card-title"><i class="pi pi-file-import" aria-hidden="true" /> Log4net — archivo (errores)</span>
      </template>
      <template #content>
        <p class="mon-hint mon-block">
          Lee el final del rolling file configurado en Web.config (p. ej. <code>logs.log*</code> en la carpeta de la
          app). Puedes fijar carpeta con appSetting <code>MONITORING_LOG_DIR</code> o patrón con
          <code>MONITORING_LOG_GLOB</code>. También existe AdoNetAppender a SQL; si lo usas, habría que exponer otra
          consulta contra esa base.
        </p>
        <MessagePrime v-if="logFile?.warnings?.length" severity="warn" class="mon-msg">
          <ul class="mon-ul">
            <li v-for="(w, i) in logFile.warnings" :key="i">{{ w }}</li>
          </ul>
        </MessagePrime>
        <dl v-if="logFile" class="mon-dl mon-dl--tight">
          <dt>Patrón</dt>
          <dd>{{ logFile.fileGlob }}</dd>
          <dt>Archivo leído</dt>
          <dd>{{ logFile.primaryFile || '—' }}</dd>
          <dt>Truncado (solo final)</dt>
          <dd>{{ logFile.truncated ? 'Sí' : 'No' }}</dd>
          <dt>Líneas devueltas</dt>
          <dd>{{ logFile.lines?.length ?? 0 }}</dd>
        </dl>
        <h3 class="mon-sub">Métricas del fragmento (nivel y hora)</h3>
        <div v-if="logFile?.metrics" class="mon-metrics-row">
          <DataTable
            v-if="logByLevelRows.length"
            :value="logByLevelRows"
            size="small"
            stripedRows
            class="mon-metrics-table"
          >
            <ColumnPrime field="nivel" header="Nivel (aprox.)" />
            <ColumnPrime field="conteo" header="Líneas" />
          </DataTable>
          <DataTable
            v-if="logByHourRows.length"
            :value="logByHourRows"
            size="small"
            stripedRows
            class="mon-metrics-table"
          >
            <ColumnPrime field="hora" header="Hora (agrupada)" />
            <ColumnPrime field="conteo" header="Líneas" />
          </DataTable>
        </div>
        <DataTable
          v-if="logHintsRows.length"
          :value="logHintsRows"
          size="small"
          stripedRows
          class="mon-card mon-metrics-full"
        >
          <ColumnPrime field="clave" header="Pista" />
          <ColumnPrime field="conteo" header="Veces" />
        </DataTable>
        <h3 v-if="logFile?.metrics?.recentErrorSamples?.length" class="mon-sub">Muestras con «ERROR»</h3>
        <ul v-if="logFile?.metrics?.recentErrorSamples?.length" class="mon-samples">
          <li v-for="(s, i) in logFile.metrics.recentErrorSamples" :key="i" class="mon-sample">{{ s }}</li>
        </ul>
        <h3 class="mon-sub">Cola del archivo (texto)</h3>
        <pre v-if="logFile?.lines?.length" class="mon-log-pre" spellcheck="false">{{ logLinesText }}</pre>
        <p v-else class="mon-muted">Sin líneas o aún no se ha cargado el log.</p>
      </template>
    </CardPrime>

    <CardPrime class="surface-card mon-card mon-card--full">
      <template #title>
        <span class="mon-card-title"><i class="pi pi-database" aria-hidden="true" /> SQL Server (DMVs)</span>
      </template>
      <template #content>
        <p class="mon-hint mon-block">
          Lectura en tiempo real. Si faltan permisos (p. ej. VIEW SERVER STATE), verás mensajes en «Errores».
          Los planes en caché reflejan acumulado histórico, no solo esta pantalla.
        </p>
        <MessagePrime v-if="sql?.errors?.length" severity="warn" class="mon-msg">
          <ul class="mon-ul">
            <li v-for="(e, i) in sql.errors" :key="i">{{ e }}</li>
          </ul>
        </MessagePrime>
        <h3 class="mon-sub">Sesiones de usuario (BD actual)</h3>
        <DataTable
          v-if="sqlRowsUserSessions.length"
          :value="sqlRowsUserSessions"
          size="small"
          stripedRows
          responsiveLayout="scroll"
        >
          <ColumnPrime v-for="col in sqlUserSessionColumns" :key="col" :field="col" :header="col" />
        </DataTable>
        <p v-else class="mon-muted">Sin filas o sin permiso.</p>

        <h3 class="mon-sub">Peticiones en ejecución (top por tiempo)</h3>
        <DataTable
          v-if="sqlRowsRunning.length"
          :value="sqlRowsRunning"
          size="small"
          stripedRows
          responsiveLayout="scroll"
        >
          <ColumnPrime v-for="col in sqlRunningColumns" :key="col" :field="col" :header="col" />
        </DataTable>
        <p v-else class="mon-muted">Sin consultas en curso visibles.</p>

        <h3 class="mon-sub">Planes en caché — mayor tiempo total acumulado</h3>
        <DataTable
          v-if="sqlRowsPlans.length"
          :value="sqlRowsPlans"
          size="small"
          stripedRows
          responsiveLayout="scroll"
        >
          <ColumnPrime v-for="col in sqlPlanColumns" :key="col" :field="col" :header="col" />
        </DataTable>
        <p v-else class="mon-muted">Sin datos de planes o sin permiso.</p>
      </template>
    </CardPrime>
  </div>
</template>

<script>
import MonitoreoService from '@/services/MonitoreoService';
import { formatApiError } from '@/utils/formatApiError';
import CardPrime from 'primevue/card';
import DataTable from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import { useToast } from 'primevue/usetoast';

export default {
  name: 'DashboardMonitoreo',
  components: { CardPrime, DataTable, ColumnPrime },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      summary: null,
      sql: null,
      logFile: null,
      logMaxLines: 400,
      logMaxKb: 512,
      loadingAll: false,
      loadingLog: false,
      loadingReset: false,
      errorMsg: '',
    };
  },
  computed: {
    logLinesText() {
      if (!this.logFile?.lines?.length) return '';
      return this.logFile.lines.join('\n');
    },
    logByLevelRows() {
      return this.dictToRows(this.logFile?.metrics?.byLevel, 'nivel', 'conteo');
    },
    logByHourRows() {
      const rows = this.dictToRows(this.logFile?.metrics?.byHour, 'hora', 'conteo');
      return rows.sort((a, b) => String(a.hora).localeCompare(String(b.hora)));
    },
    logHintsRows() {
      return this.dictToRows(this.logFile?.metrics?.hints, 'clave', 'conteo');
    },
    client() {
      const perf = typeof performance !== 'undefined' ? performance : null;
      const m = perf && perf.memory;
      return {
        href: typeof window !== 'undefined' ? window.location.href : '',
        apiBase: process.env.VUE_APP_API_URL || '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        jsHeapMb: m ? Math.round((m.usedJSHeapSize / 1024 / 1024) * 10) / 10 : null,
        jsHeapLimitMb: m ? Math.round((m.jsHeapSizeLimit / 1024 / 1024) * 10) / 10 : null,
      };
    },
    sqlRowsUserSessions() {
      return Array.isArray(this.sql?.userSessions) ? this.sql.userSessions : [];
    },
    sqlRowsRunning() {
      return Array.isArray(this.sql?.runningRequests) ? this.sql.runningRequests : [];
    },
    sqlRowsPlans() {
      return Array.isArray(this.sql?.topPlansByElapsed) ? this.sql.topPlansByElapsed : [];
    },
    sqlUserSessionColumns() {
      return this.columnKeys(this.sqlRowsUserSessions);
    },
    sqlPlanColumns() {
      return this.columnKeys(this.sqlRowsPlans);
    },
  },
  mounted() {
    this.cargarTodo();
  },
  methods: {
    columnKeys(rows) {
      if (!rows.length) return [];
      return Object.keys(rows[0]);
    },
    fmtUtc(iso) {
      if (!iso) return '—';
      try {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? String(iso) : d.toISOString().replace('T', ' ').slice(0, 23);
      } catch {
        return String(iso);
      }
    },
    roundMs(n) {
      if (n == null || Number.isNaN(n)) return '—';
      return Math.round(n * 10) / 10;
    },
    dictToRows(obj, keyField, valField) {
      if (!obj || typeof obj !== 'object') return [];
      return Object.keys(obj).map((k) => ({ [keyField]: k, [valField]: obj[k] }));
    },
    async cargarSoloLogs(silent = false) {
      if (!silent) this.loadingLog = true;
      try {
        const lf = await MonitoreoService.getLogFile(this.logMaxLines, this.logMaxKb);
        this.logFile = lf.data;
      } catch (e) {
        this.logFile = null;
        this.toast.add({
          severity: 'warn',
          summary: 'Log de archivo',
          detail: formatApiError(e, 'No se pudo leer el archivo de log'),
          life: silent ? 4000 : 7000,
        });
      } finally {
        if (!silent) this.loadingLog = false;
      }
    },
    async cargarTodo() {
      this.errorMsg = '';
      this.loadingAll = true;
      try {
        const [s, q] = await Promise.all([
          MonitoreoService.getSummary(),
          MonitoreoService.getSqlSnapshot(),
        ]);
        this.summary = s.data;
        this.sql = q.data;
      } catch (e) {
        this.errorMsg = formatApiError(e, 'No se pudo cargar el monitoreo');
      }
      try {
        await this.cargarSoloLogs(true);
      } finally {
        this.loadingAll = false;
      }
    },
    confirmarReinicio() {
      if (!window.confirm('¿Reiniciar contadores y el historial de peticiones en el servidor? (No borra datos en SQL.)')) {
        return;
      }
      this.reiniciarMetricas();
    },
    async reiniciarMetricas() {
      this.errorMsg = '';
      this.loadingReset = true;
      try {
        await MonitoreoService.resetDiagnostics();
        this.toast.add({ severity: 'success', summary: 'Métricas reiniciadas', life: 3000 });
        await this.cargarTodo();
      } catch (e) {
        this.errorMsg = formatApiError(e, 'No se pudo reiniciar');
      } finally {
        this.loadingReset = false;
      }
    },
  },
};
</script>

<style scoped>
.mon-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem 2.5rem;
}

.mon-hero {
  margin-bottom: 1.25rem;
}

.mon-eyebrow {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--admin-muted, #64748b);
  margin: 0 0 0.35rem;
}

.mon-title {
  font-size: 1.65rem;
  margin: 0 0 0.5rem;
  color: var(--admin-heading, #0f172a);
}

.mon-lead {
  margin: 0;
  max-width: 52rem;
  line-height: 1.55;
  color: var(--admin-body, #334155);
}

.mon-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 1rem;
}

.mon-msg {
  margin-bottom: 1rem;
}

.mon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.mon-card--wide {
  grid-column: 1 / -1;
}

.mon-card--full {
  margin-bottom: 1rem;
}

.mon-card-title {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.mon-dl {
  display: grid;
  grid-template-columns: minmax(8rem, 11rem) 1fr;
  gap: 0.35rem 0.75rem;
  margin: 0;
  font-size: 0.9rem;
}

.mon-dl dt {
  color: var(--admin-muted, #64748b);
  font-weight: 600;
}

.mon-dl dd {
  margin: 0;
  word-break: break-word;
}

.mon-wrap {
  word-break: break-word;
}

.mon-muted {
  color: var(--admin-muted, #64748b);
  margin: 0;
}

.mon-hint {
  font-size: 0.8rem;
  color: var(--admin-muted, #64748b);
  font-weight: 400;
}

.mon-block {
  margin: 0 0 1rem;
}

.mon-sub {
  font-size: 1rem;
  margin: 1.25rem 0 0.5rem;
  color: var(--admin-heading, #0f172a);
}

.mon-ul {
  margin: 0;
  padding-left: 1.1rem;
}

.mon-log-params {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  margin-left: auto;
}

.mon-log-label {
  font-size: 0.85rem;
  color: var(--admin-muted, #64748b);
}

.mon-select {
  padding: 0.35rem 0.5rem;
  border-radius: 6px;
  border: 1px solid var(--admin-border, rgba(15, 23, 42, 0.12));
  background: #fff;
}

.mon-dl--tight {
  margin-bottom: 0.75rem;
}

.mon-metrics-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.mon-metrics-table {
  min-height: 0;
}

.mon-metrics-full {
  margin-bottom: 1rem;
}

.mon-samples {
  margin: 0 0 1rem;
  padding-left: 1.1rem;
  font-size: 0.8rem;
  color: var(--admin-body, #334155);
}

.mon-sample {
  margin-bottom: 0.35rem;
  word-break: break-word;
}

.mon-log-pre {
  margin: 0;
  padding: 0.75rem 1rem;
  max-height: 28rem;
  overflow: auto;
  font-size: 0.75rem;
  line-height: 1.45;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
